/* eslint-disable react/prop-types */
"use client";

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageBubble } from "./ChatFeed";
import { CHAT_REACTION_OPTIONS } from "../../utils/chatReactions";

const VIEWPORT_PADDING = 12;

function readVisualViewport() {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  const viewport = window.visualViewport;
  return {
    left: viewport?.offsetLeft || 0,
    top: viewport?.offsetTop || 0,
    width: viewport?.width || window.innerWidth,
    height: viewport?.height || window.innerHeight,
  };
}

function useVisualViewport(active) {
  const [viewport, setViewport] = useState(readVisualViewport);

  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;

    const update = () => setViewport(readVisualViewport());
    const visualViewport = window.visualViewport;

    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    visualViewport?.addEventListener?.("resize", update);
    visualViewport?.addEventListener?.("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      visualViewport?.removeEventListener?.("resize", update);
      visualViewport?.removeEventListener?.("scroll", update);
    };
  }, [active]);

  return viewport;
}

export default function MobileMessageActionLayer({
  item,
  rowRect,
  actions,
  canReact,
  selectedReactions,
  onToggleReaction,
  onClose,
  variant,
  detailPanel = null,
  t,
}) {
  const viewport = useVisualViewport(!!item);
  const safeFrameRef = useRef(null);
  const reactionsRef = useRef(null);
  const controlsRef = useRef(null);
  const [measuredMessageMaxHeight, setMeasuredMessageMaxHeight] = useState(null);

  useEffect(() => {
    if (!item || typeof document === "undefined") return undefined;

    const bodyOverflow = document.body.style.overflow;
    const documentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.classList.add("vg-chat-context-open");
    document.documentElement.classList.remove("vg-chat-long-press-gesture");

    const clearNativeSelection = () => {
      try {
        window.getSelection?.()?.removeAllRanges?.();
      } catch {
        // Selection cleanup is best-effort in native WebViews.
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const frameId = window.requestAnimationFrame(clearNativeSelection);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("selectionchange", clearNativeSelection);

    return () => {
      window.cancelAnimationFrame(frameId);
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = documentOverflow;
      document.documentElement.classList.remove("vg-chat-context-open");
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("selectionchange", clearNativeSelection);
    };
  }, [item, onClose]);

  const entryOffset = useMemo(() => {
    if (!rowRect || !viewport.height) return 0;
    const sourceCenter = rowRect.top + rowRect.height / 2;
    const viewportCenter = viewport.top + viewport.height / 2;
    return Math.max(-72, Math.min(72, sourceCenter - viewportCenter));
  }, [rowRect, viewport.height, viewport.top]);

  const selectedMessageGeometry = useMemo(() => {
    const measuredWidth = Number(rowRect?.width || 0);
    const measuredLeft = Number(rowRect?.left || 0) - viewport.left;
    const width = Math.min(
      viewport.width,
      measuredWidth > 0 ? measuredWidth : viewport.width
    );

    return {
      left: Math.min(Math.max(measuredLeft, 0), Math.max(0, viewport.width - width)),
      width,
    };
  }, [rowRect, viewport.left, viewport.width]);

  const fallbackMessageMaxHeight = useMemo(() => {
    const reactionHeight = canReact ? 52 : 0;
    const actionRowsHeight = Math.max(0, actions.length * 40 + actions.length - 1);
    const actionPanelHeight = Math.min(
      actionRowsHeight,
      Math.min(viewport.height * 0.34, 280)
    );
    const detailPanelReserve = detailPanel ? Math.min(viewport.height * 0.22, 180) + 8 : 0;

    return Math.max(
      80,
      Math.floor(
        viewport.height -
          VIEWPORT_PADDING * 2 -
          reactionHeight -
          actionPanelHeight -
          detailPanelReserve -
          8
      )
    );
  }, [actions.length, canReact, detailPanel, viewport.height]);

  useLayoutEffect(() => {
    if (!item || typeof window === "undefined") return undefined;

    const measureAvailableMessageHeight = () => {
      const safeFrame = safeFrameRef.current;
      if (!safeFrame) return;

      const frameHeight = safeFrame.clientHeight || viewport.height;
      if (!frameHeight) return;

      const frameStyles = window.getComputedStyle(safeFrame);
      const paddingTop = Number.parseFloat(frameStyles.paddingTop) || VIEWPORT_PADDING;
      const paddingBottom = Number.parseFloat(frameStyles.paddingBottom) || VIEWPORT_PADDING;
      const reactionsHeight = reactionsRef.current?.offsetHeight || 0;
      const controlsHeight = controlsRef.current?.offsetHeight || 0;
      const nextHeight = Math.max(
        80,
        Math.floor(
          frameHeight - paddingTop - paddingBottom - reactionsHeight - controlsHeight
        )
      );

      setMeasuredMessageMaxHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    };

    measureAvailableMessageHeight();
    const frameId = window.requestAnimationFrame(measureAvailableMessageHeight);
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(measureAvailableMessageHeight)
        : null;

    [safeFrameRef.current, reactionsRef.current, controlsRef.current].forEach((element) => {
      if (element) resizeObserver?.observe(element);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [actions.length, canReact, detailPanel, item, viewport.height, viewport.width]);

  if (!item) return null;

  return (
    <motion.div
      data-testid="mobile-message-action-layer"
      className="fixed z-[95] overflow-hidden"
      style={{
        left: viewport.left,
        top: viewport.top,
        width: viewport.width,
        height: viewport.height,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t("chats.event_chat.ctx.message_actions", {
        defaultValue: "Message actions",
      })}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.14, ease: "easeOut" }}
    >
      <button
        type="button"
        data-testid="chat-context-backdrop"
        className="absolute inset-0 h-full w-full cursor-default bg-black/[0.86] backdrop-blur-[22px]"
        style={{
          WebkitBackdropFilter: "blur(22px)",
          backdropFilter: "blur(22px)",
        }}
        onPointerDown={onClose}
        aria-label={t("chats.event_chat.ctx.close", { defaultValue: "Close message actions" })}
      />

      <div
        ref={safeFrameRef}
        className="pointer-events-none absolute inset-0 flex min-h-0 items-center justify-center px-3"
        style={{
          paddingTop: `calc(env(safe-area-inset-top, 0px) + ${VIEWPORT_PADDING}px)`,
          paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${VIEWPORT_PADDING}px)`,
        }}
      >
        <div
          className="flex max-h-full w-full min-h-0 flex-col"
          data-testid="chat-overlay-content-stack"
        >
          {canReact ? (
            <motion.div
              ref={reactionsRef}
              data-testid="chat-overlay-reactions"
              className="pointer-events-auto flex shrink-0 justify-center pb-2"
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <div className="flex h-11 items-center justify-center gap-0.5 rounded-[22px] bg-zinc-800/95 px-2 shadow-[0_12px_30px_rgba(0,0,0,0.32)]">
                {CHAT_REACTION_OPTIONS.map((option) => {
                  const isSelected = selectedReactions.has(option.emoji);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        onToggleReaction?.(item, option.emoji);
                        onClose?.();
                      }}
                      className={`flex h-8 w-8 touch-manipulation items-center justify-center rounded-full text-[18px] transition active:scale-[0.94] ${
                        isSelected ? "bg-[#3FA3FF]" : "bg-white/10"
                      }`}
                      aria-label={t(option.labelKey)}
                    >
                      <span aria-hidden="true">{option.emoji}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ) : null}

          <div
            data-testid="chat-overlay-message-viewport"
            className="pointer-events-auto -mx-3 min-h-0 w-[calc(100%+1.5rem)] shrink overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch]"
            style={{
              maxHeight: measuredMessageMaxHeight || fallbackMessageMaxHeight,
              touchAction: "pan-y",
            }}
          >
            <motion.div
              data-testid="chat-overlay-selected-message"
              className="pointer-events-auto box-border select-none"
              style={{
                marginLeft: selectedMessageGeometry.left,
                width: selectedMessageGeometry.width,
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              }}
              initial={{ opacity: 0.84, y: entryOffset }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <MessageBubble
                item={item}
                disableInteractions
                variant={variant}
                hideSenderBadge={variant === "direct"}
              />
            </motion.div>
          </div>

          <motion.div
            ref={controlsRef}
            className="pointer-events-none flex min-h-0 shrink-0 flex-col items-end gap-2 pt-2"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {detailPanel}

            <div
              data-chat-overlay-section="actions"
              data-testid="chat-overlay-actions"
              className="pointer-events-auto w-36 select-none overflow-y-auto rounded-2xl bg-zinc-800/95 shadow-[0_14px_32px_rgba(0,0,0,0.32)]"
              style={{ maxHeight: "min(34dvh, 280px)" }}
              role="menu"
            >
              {actions.map((action, index) => (
                <React.Fragment key={action.key}>
                  <button
                    type="button"
                    onClick={action.onClick}
                    className="flex min-h-10 w-full touch-manipulation items-center justify-between px-3 py-2 text-left active:bg-white/10"
                    aria-label={action.label}
                    role="menuitem"
                  >
                    <span
                      className={`${
                        action.danger ? "text-red-500" : "text-white"
                      } text-xs font-medium leading-4`}
                    >
                      {action.label}
                    </span>

                    {action.icon ? (
                      <img src={action.icon} alt="" className="h-4 w-4 opacity-90" />
                    ) : action.iconNode ? (
                      <span className="flex h-4 w-4 items-center justify-center">
                        {action.iconNode}
                      </span>
                    ) : null}
                  </button>

                  {index < actions.length - 1 ? <div className="h-px bg-white/20" /> : null}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
